import { zodResolver } from "@hookform/resolvers/zod";
import { ActionIcon, Button, Card, Stack, Title, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { FunnelIcon, InfoIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import z from "zod";

import { Input } from "@/components/_Input";
import { DataGridBase } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { FilterPagination, Logs, useGetLogFiltersQuery, useListLogsInfiniteQuery } from "@/store";
import { transformInOptions } from "@/utils";

export const Route = createFileRoute("/_private/dashboard/_base/logs/")({
  component: RouteComponent,
  head: () => ({
    meta: [{ title: "Logs" }]
  })
});

type Schema = z.infer<typeof LogSchema>;
export const LogSchema = z
  .object({
    user: z.string().optional().nullable(),
    company: z.string().optional().nullable(),
    object: z.string().optional().nullable(),
    operation: z.string().optional().nullable(),
    dateRange: z.tuple([z.string(), z.string()])
  })
  .superRefine((val, ctx) => {
    const someSelected = Object.values(val).some((value) => value !== undefined && value !== null && value !== "");

    if (!someSelected) {
      const message = "Selecione ao menos um filtro";
      toast.error(message);

      ctx.addIssue({
        code: "custom",
        message,
        path: []
      });
    }
  });

function RouteComponent() {
  const [searchParams, setSearchParams] = useState<FilterPagination>({
    page: 1,
    pageSize: 30,
    startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD")
  });

  const { data, isFetching, refetch: refetchLogs, fetchNextPage, hasNextPage } = useListLogsInfiniteQuery({ params: searchParams });
  const {
    data: filtersData,
    isFetching: isFetchingFilters,
    isError: isErrorFilters,
    refetch: refetchFilters
  } = useGetLogFiltersQuery(undefined, {
    selectFromResult: (state) => {
      return {
        ...state,
        data: {
          ...state.data,
          users: transformInOptions(state.data?.users),
          companies: transformInOptions(state.data?.companies),
          objects: transformInOptions(state.data?.objects, { getLabel: (item) => item.label }),
          operations: transformInOptions(state.data?.operations, { getLabel: (item) => item.label ?? "N/A" })
        }
      };
    }
  });

  const handleRefetch = () => {
    refetchLogs();
    refetchFilters();
  };

  const { handleSubmit, control } = useForm({
    mode: "all",
    resolver: zodResolver(LogSchema),
    disabled: isFetchingFilters || isFetching,
    defaultValues: {
      user: "",
      company: "",
      object: "",
      operation: "",
      dateRange: [dayjs().subtract(7, "day").format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")]
    }
  });
  const onFilter: SubmitHandler<Schema> = (data) => {
    const {
      dateRange: [startDate, endDate]
    } = data;

    setSearchParams((prev) => ({
      ...prev,
      userId: data.user ? Number(data.user) : 0,
      companyId: data.company ? Number(data.company) : 0,
      object_type: (data.object as App.Objects.Id) || "",
      operation_type: (data.operation as App.Operations.Id) || "",
      startDate,
      endDate
    }));
  };

  const columns: GridColDef<Logs>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 100,
      align: "center",
      headerAlign: "center"
    },
    {
      field: "title",
      headerName: "Título",
      flex: 1,
      type: "string",
      renderCell: (params) => {
        return <div dangerouslySetInnerHTML={{ __html: params.value }} />;
      }
    },
    {
      field: "company",
      headerName: "Empresa",
      type: "string",
      minWidth: 200
    },
    {
      field: "details",
      type: "actions",
      headerName: "Ações",
      renderCell: ({ value, row }) => {
        const hasDetails = value.length > 0;
        const handleDetails = () => {
          if (value.length === 0) return;

          modals.open({
            title: "Detalhes",
            children: (
              <Stack mah={400} className="overflow-y-auto">
                {row.details.map(({ key, value }, i) => (
                  <p className="text-sm" key={i}>
                    <strong className="font-semibold">{key}: </strong>
                    {value}
                  </p>
                ))}
              </Stack>
            )
          });
        };

        return (
          <Tooltip label={hasDetails ? "Ver detalhes" : "Não há detalhes"}>
            <ActionIcon variant="light" color="blue" radius="xl" onClick={handleDetails} disabled={!hasDetails}>
              <InfoIcon />
            </ActionIcon>
          </Tooltip>
        );
      }
    }
  ];
  const rows = data?.pages.flatMap((page) => page.data) || [];

  return (
    <Stack>
      <div className="flex items-center justify-between">
        <DashboardComponents.Title>Logs</DashboardComponents.Title>

        <div className="flex items-center gap-1">
          <Button onClick={fetchNextPage} disabled={!hasNextPage}>
            Carregar Mais
          </Button>
          <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Logs" }} onClick={handleRefetch} loading={isFetching} />
        </div>
      </div>

      <Card mb="md" withBorder>
        <Stack>
          <Title order={4}>Filtros</Title>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Input.Select control={control} placeholder="Selecione" name="user" label="Usuário" data={filtersData.users} />
            <Input.Select control={control} placeholder="Selecione" name="company" label="Empresa" data={filtersData.companies} />
            <Input.Select control={control} placeholder="Selecione" name="object" label="Objeto" data={filtersData.objects} />
            <Input.Select control={control} placeholder="Selecione" name="operation" label="Operação" data={filtersData.operations} />
          </div>

          <div className="flex flex-col gap-4">
            <Title order={5}>Período</Title>

            <Input.DateRange
              name="dateRange"
              control={control}
              numberOfColumns={2}
              allowSingleDateInRange
              maxDate={dayjs().format("YYYY-MM-DD")}
              size="sm"
              firstDayOfWeek={0}
              locale="pt-BR"
              showLegend
              allowMultipleHighlights={false}
              className="mt-4 self-center md:self-start"
            />
          </div>

          <Button
            color="red"
            loading={isFetching || isFetchingFilters}
            leftSection={<FunnelIcon weight="fill" />}
            disabled={isErrorFilters}
            onClick={handleSubmit(onFilter)}
            className="self-end"
          >
            Filtrar
          </Button>
        </Stack>
      </Card>

      <DataGridBase rows={rows} columns={columns} loading={isFetching} />
    </Stack>
  );
}
